import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  contentCollectionAutoSubmitStatusByUploadIdSelector,
  contentCollectionHasFailedAutoSubmitSelector,
  setAutoSubmitStatusAction,
} from '@air/feature-content-collection/redux';
import { FileStatus, SHARED_PANEL_ITEM_CLASS_NAMES } from '@air/feature-file-status/constants';
import {
  FileStatusIcon,
  FileStatusPanelItem,
  FileStatusSubtitle,
  FileStatusThumbnail,
  FileStatusTitle,
  FileStatusTrackingPanelListItemCompleted,
  FileStatusTrackingPanelListItemErrored,
  type FileStatusTrackingPanelListItemErroredProps,
  FileStatusTrackingPanelListItemPaused,
  FileStatusTrackingPanelListItemProgressing,
  type FileStatusTrackingPanelProps,
  progressingStatuses,
} from '@air/feature-file-status/ui';
import { UploadPreviewLoaders, UploadsTrackingPanel, type UploadsTrackingPanelProps } from '@air/feature-uploads/ui';
import { getUploadHeaderTitles } from '@air/feature-uploads/utils';
import { UploadStatus, uploadByIdSelector } from '@air/redux-uploader';
import { cn } from '@air/utils-tailwind';

import { useContentCollectionUploadDelete } from '~/components/ContentCollection/hooks/useContentCollectionUploadDelete';
import { useAirStore } from '~/utils/ReduxUtils';

export const ContentCollectionAutoSubmitUploadsTrackingPanel = memo(() => {
  const store = useAirStore();
  const dispatch = useDispatch();
  const autoSubmitStatusByUploadId = useSelector(contentCollectionAutoSubmitStatusByUploadIdSelector);
  const hasFailedAutoSubmit = useSelector(contentCollectionHasFailedAutoSubmitSelector);
  const { onDeleteUpload: handleDelete } = useContentCollectionUploadDelete();

  const getCompletedItemsToDisplay = useCallback<Required<FileStatusTrackingPanelProps>['getCompletedItemsToDisplay']>(
    ({ items }) => {
      return items
        .filter(
          (item) => item.status === UploadStatus.completed && autoSubmitStatusByUploadId[item.id]?.status === 'success',
        )
        .map((item) => <FileStatusTrackingPanelListItemCompleted item={item} key={item.id} />);
    },
    [autoSubmitStatusByUploadId],
  );

  const getErroredItemsToDisplay = useCallback<Required<FileStatusTrackingPanelProps>['getErroredItemsToDisplay']>(
    ({ items, onCancelItem, onRetryItem }) => {
      return items
        .filter(
          (item) => item.status === UploadStatus.failed || autoSubmitStatusByUploadId[item.id]?.status === 'failure',
        )
        .map((item) => {
          const itemProps: FileStatusTrackingPanelListItemErroredProps = {
            item,
            error: item.error ?? '',
            onCancel: onCancelItem,
            onRetry: onRetryItem,
          };

          const autoSubmitStatus = autoSubmitStatusByUploadId[item.id];

          if (autoSubmitStatus?.status === 'failure') {
            const upload = uploadByIdSelector(store.getState(), item.id);
            itemProps.error = autoSubmitStatus.message;
            itemProps.onCancel = undefined;
            itemProps.onRetry =
              autoSubmitStatus.retryable && upload
                ? () => dispatch(setAutoSubmitStatusAction({ clipId: upload.clipId, status: 'pending' }))
                : undefined;
          }

          return <FileStatusTrackingPanelListItemErrored key={item.id} {...itemProps} />;
        });
    },
    [autoSubmitStatusByUploadId, dispatch, store],
  );

  const getOngoingItemsToDisplay = useCallback<Required<FileStatusTrackingPanelProps>['getOngoingItemsToDisplay']>(
    ({ items, onCancelItem, onPauseItem, onResumeItem }) => {
      return items
        .filter(
          (item) =>
            [...progressingStatuses, UploadStatus.paused].includes(item.status) ||
            (autoSubmitStatusByUploadId[item.id]?.status === 'pending' && item.status === UploadStatus.completed),
        )
        .map((item) => {
          if (autoSubmitStatusByUploadId[item.id]?.status === 'pending' && item.status === UploadStatus.completed) {
            return (
              <FileStatusPanelItem
                key={item.id}
                avatar={
                  <FileStatusThumbnail
                    ext={item.ext}
                    status={UploadStatus.uploading}
                    thumbnailSource={item.thumbnailSource}
                  />
                }
                buttons={undefined}
                className={cn('ads:hover:bg-hover-subtle hover:bg-gray-3', SHARED_PANEL_ITEM_CLASS_NAMES)}
                subtitle={<FileStatusSubtitle>Finalizing...</FileStatusSubtitle>}
                title={<FileStatusTitle>{item.title}</FileStatusTitle>}
              />
            );
          }

          if (item.status === UploadStatus.paused) {
            return (
              <FileStatusTrackingPanelListItemPaused
                item={item}
                key={item.id}
                onCancel={onCancelItem}
                onResume={onResumeItem}
              />
            );
          }

          return (
            <FileStatusTrackingPanelListItemProgressing
              item={item}
              key={item.id}
              onCancel={onCancelItem}
              onPause={onPauseItem}
            />
          );
        });
    },
    [autoSubmitStatusByUploadId],
  );

  const onRetryAll = useCallback(
    ({ retryAllUploads }: { retryAllUploads: () => void }) => {
      const failedSubmissionUploadIds = Object.keys(autoSubmitStatusByUploadId).filter(
        (uploadId) => autoSubmitStatusByUploadId[uploadId]?.status === 'failure',
      );

      failedSubmissionUploadIds.forEach((uploadId) => {
        const upload = uploadByIdSelector(store.getState(), uploadId);

        if (upload) {
          dispatch(setAutoSubmitStatusAction({ clipId: upload.clipId, status: 'pending' }));
        }
      });

      retryAllUploads();
    },
    [autoSubmitStatusByUploadId, dispatch, store],
  );

  const getStatusTitle = useCallback<NonNullable<UploadsTrackingPanelProps['getStatusTitle']>>(
    (args) => {
      const { status } = args;

      if (status === FileStatus.completed && hasFailedAutoSubmit) {
        return getUploadHeaderTitles(args)[FileStatus.errored];
      }

      return getUploadHeaderTitles(args)[status];
    },
    [hasFailedAutoSubmit],
  );

  const getSummaryTrackingAvatar = useCallback<NonNullable<UploadsTrackingPanelProps['getSummaryTrackingAvatar']>>(
    (_, status) => {
      if (status === FileStatus.completed && hasFailedAutoSubmit) {
        return <FileStatusIcon status={FileStatus.errored} />;
      }

      return <FileStatusIcon status={status} />;
    },
    [hasFailedAutoSubmit],
  );

  return (
    <>
      <UploadPreviewLoaders />
      <UploadsTrackingPanel
        summaryLocation={undefined}
        isExpanded
        getOngoingItemsToDisplay={getOngoingItemsToDisplay}
        getCompletedItemsToDisplay={getCompletedItemsToDisplay}
        getErroredItemsToDisplay={getErroredItemsToDisplay}
        disableDismiss
        disableToggle
        onRetryAll={onRetryAll}
        onDeleteUpload={handleDelete}
        getStatusTitle={getStatusTitle}
        getSummaryTrackingAvatar={getSummaryTrackingAvatar}
        className="w-full md:w-[370px]"
        style={{ width: undefined }}
      />
    </>
  );
});

ContentCollectionAutoSubmitUploadsTrackingPanel.displayName = 'ContentCollectionAutoSubmitUploadsTrackingPanel';
